import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import LoadProgress from '@/components/LoadProgress'

import { DataGridPremium, GridColDef, useGridApiRef } from '@mui/x-data-grid-premium'

import { memberRevisionsQueryOptions } from '@/api/memberQueryOptions'
import { useColumnState } from '@/components/hooks/useColumnState'
import { DetailGridToolbar } from '@/components/mui-x/datagrid/DetailGridToolbar'
import MemberJobRevision from '@/components/revision/MemberJobRevision'
import MemberRelationRevision from '@/components/revision/MemberRelationRevision'
import MemberSectionRevision from '@/components/revision/MemberSectionRevision'
import MembershipDynamicFieldRevision from '@/components/revision/MembershipDynamicFieldRevision'
import RevisionChange from '@/components/revision/RevisionChange'
import RevisionTypeIcon from '@/components/revision/RevisionTypeIcon'
import Box from '@mui/material/Box'
import { t } from 'i18next'

export const Route = createFileRoute('/_auth/member_/$memberUuid/revisions')({
    component: MemberRevisionsComponent,
})

function MemberRevisionsComponent() {
    const params = Route.useParams()
    const memberRevisionsQuery = useSuspenseQuery(
        memberRevisionsQueryOptions(params.memberUuid),
    )
    const memberRevisions = memberRevisionsQuery.data

    // const basicGridColumns = useBasicGridColumns()
    // const { xsmall, small, medium, large, xlarge } = useResponsive()

    const columns: GridColDef[] = []
    columns.push({
        field: 'metadata.revisionInstant',
        valueGetter: (_value, row) => row.metadata.revisionInstant && new Date(row.metadata.revisionInstant),
        type: 'dateTime',
        headerName: t('property.revision.revisionInstant'),
        minWidth: 125,
    },
        {
            field: 'username',
            valueGetter: (_value, row) => `${row.metadata.username || ''}`,
            headerName: t('property.revision.username'),
            minWidth: 125,
        },
        {
            field: 'changes',
            headerName: t('property.revision.changes'),
            minWidth: 125,
            valueGetter: (_value, row) => {
                return row.changes && row.changes.map((change: any) => {
                    return t('entity.' + row.entityType) + ': ' + t('property.' + row.entityType + '.' + change.propertyName, { defaultValue: t('property.general.' + change.propertyName, { defaultValue: change.propertyName }) })
                }).join(', ')
            },
            renderCell: (params) => {
                return (
                    <Box>
                        {params.row.metadata.revisionType == 'UPDATE' && (
                            <>
                                <RevisionTypeIcon revisionType={params.row.metadata.revisionType} />
                                <MemberJobRevision revisionType={params.row.metadata.revisionType} entityType={params.row.entityType} entity={params.row.entity} />
                                <MemberSectionRevision revisionType={params.row.metadata.revisionType} entityType={params.row.entityType} entity={params.row.entity} />
                                <MemberRelationRevision revisionType={params.row.metadata.revisionType} entityType={params.row.entityType} entity={params.row.entity} />
                                <MembershipDynamicFieldRevision revisionType={params.row.metadata.revisionType} entityType={params.row.entityType} entity={params.row.entity} />
                                {params.row.changes.map((change: any, index: number) => (
                                    <RevisionChange entityType={params.row.entityType} entity={params.row.entity} change={change} count={params.row.changes.length} index={index} key={index} />
                                ))
                                }
                            </>
                        )
                        }

                        {params.row.metadata.revisionType == 'DELETE' && (
                            <Box>
                                <RevisionTypeIcon revisionType={params.row.metadata.revisionType} />
                                <MemberJobRevision revisionType={params.row.metadata.revisionType} entityType={params.row.entityType} entity={params.row.entity} />
                                <MemberSectionRevision revisionType={params.row.metadata.revisionType} entityType={params.row.entityType} entity={params.row.entity} />
                                <MemberRelationRevision revisionType={params.row.metadata.revisionType} entityType={params.row.entityType} entity={params.row.entity} />
                                <MembershipDynamicFieldRevision revisionType={params.row.metadata.revisionType} entityType={params.row.entityType} entity={params.row.entity} />

                            </Box>
                        )}
                        {params.row.metadata.revisionType == 'INSERT' && (
                            <Box>
                                <RevisionTypeIcon revisionType={params.row.metadata.revisionType} />
                                <MemberJobRevision revisionType={params.row.metadata.revisionType} entityType={params.row.entityType} entity={params.row.entity} />
                                <MemberSectionRevision revisionType={params.row.metadata.revisionType} entityType={params.row.entityType} entity={params.row.entity} />
                                <MemberRelationRevision revisionType={params.row.metadata.revisionType} entityType={params.row.entityType} entity={params.row.entity} />
                                <MembershipDynamicFieldRevision revisionType={params.row.metadata.revisionType} entityType={params.row.entityType} entity={params.row.entity} />
                            </Box>
                        )}
                    </Box>
                )
            }
        })

    const CustomToolbar = () => {
        return (
            <DetailGridToolbar scope={'memberRevisions'} getScopeName={() => { return 'Änderungen' }} />
        )
    }

    const apiRef = useGridApiRef()
    const columnsState = useColumnState('member.revisions', apiRef, columns)

    if (memberRevisionsQuery.isLoading || memberRevisionsQuery.isFetching) {
        return <LoadProgress />
    }

    return (
        <>
            <Box sx={{
                height: { xs: `calc(100% - 50px)` }
            }}>
                <DataGridPremium
                    columns={columnsState.columns}
                    onColumnWidthChange={columnsState.onColumnWidthChange}
                    onColumnOrderChange={columnsState.onColumnOrderChange}
                    rows={memberRevisions}
                    getRowId={(row) => {
                        return row.metadata.revisionNumber || ''
                    }}
                    pageSizeOptions={[5, 15, 25, 50, 100]}
                    pagination
                    slots={{ toolbar: CustomToolbar }}
                    slotProps={{
                        toolbar: {
                            csvOptions: {
                                disableToolbarButton: true
                            },
                            printOptions: {
                                disableToolbarButton: true
                            },
                            excelOptions: {
                                disableToolbarButton: true
                            }
                        }
                    }}
                    density="compact"
                    getRowHeight={() => 'auto'}
                    hideFooter={true}
                    disableAggregation
                    disableColumnPinning
                    disableColumnReorder
                    disableMultipleRowSelection
                    disableDensitySelector
                    showToolbar />
            </Box>
        </>
    );
}
